import React from 'react';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getCompanionDeviceId } from 'flow/companion/companion-device-id';
import {
    selectDesignatedCompanionDevice,
    selectUseAsCompanion,
} from 'flow/selectors/app-selectors';

/**
 * Names the machine currently holding the companion role, on every other machine.
 *
 * The one thing a second install needs to be told. The role is account-wide and exactly one machine
 * has it, so starting a new desktop app and turning "Use as Companion" on silently takes it from
 * somewhere else - and without this the user cannot see that they are about to, or why this machine
 * is off when they expected it to be on. Shown here rather than as a prompt at startup because this
 * is where they come to turn it on, and it is only a question while they are asking it.
 *
 * Taking over is the toggle above; nothing new is needed for that. Deliberately silent in every
 * other case - when this machine holds the role, when nothing does, and when the list has not
 * arrived - so it reads as an answer to a question rather than a status line.
 */
const SettingsCompanionOtherDesignated = () => {
    const { i18next } = useAppContext();
    const useAsCompanion = useAppSelector(selectUseAsCompanion);
    const designated = useAppSelector(selectDesignatedCompanionDevice);

    if (useAsCompanion || !designated) {
        return null;
    }

    // This install opted out while still holding the role: the role has not moved, so there is no
    // other machine to name.
    if (designated.get('deviceId') === getCompanionDeviceId()) {
        return null;
    }

    return (
        <div className="px-2 pb-4 text-gray-600">
            {i18next.t('settings.general.companion.otherDesignated', {
                name: designated.get('name') || designated.get('deviceId'),
            })}
        </div>
    );
};

export default SettingsCompanionOtherDesignated;
